<script lang="ts" context="module">
    import fuzzysort from "fuzzysort";

    export type SearchTab = { name: string; items: SearchItem[] };
    export type SearchItem = { name: string; group?: string; action: () => void, enabled?: { value: boolean } };
</script>

<script lang="ts">
    import Tabs from "./Tabs.svelte";
    import type { Snippet } from "svelte";

    const maxItems = 15;

    type ItemTemplateInput = {
        item: SearchItem,
        match: string,
        group?: string,
        selected: boolean,
        tab: number,
    };
    type Props = {
        open: boolean,
        tabs?: SearchTab[],
        items: SearchItem[],
        item?: Snippet<[ItemTemplateInput]>,
    };

    let {
        open = $bindable(),
        tabs,
        items,
        item
    }: Props = $props();

    $effect(() => {
        if (open) onOpen();
    });

    let tabIndex = $state(0);
    $effect(() => {
        if (!tabs) return;
        selectedIndex = 0;
    });

    const currentItems = $derived(tabs?.[tabIndex].items ?? items);

    (() => {
        if (!currentItems) {
            throw new Error("Search: Either items or tabs must be provided.");
        }
    })();

    let selectedIndex = $state(0);
    $effect(() => {
        scrollSelectedIntoView(selectedIndex);
    });

    let query = $state("");
    $effect(() => {
        onQueryChange(query);
    });

    let results = $derived(doFilter(currentItems, query));

    function doFilter(items: SearchItem[], query: string) {
        if (!open) return [];

        const results = fuzzysort.go(query, items, {
            key: "name",
            all: true
        }) as unknown as ReadonlyArray<Fuzzysort.KeyResult<SearchItem> & {
            name: string;
            group?: string;
            disabled: boolean
        }>;

        if (query) {
            for (let result of results) {
                result.name = fuzzysort.highlight(result)!;
            }
        } else {
            for (let result of results) {
                result.name = result.obj.name;
            }
        }

        for (let result of results) {
            result.disabled = !(result.obj.enabled?.value ?? true);
        }
        return results;
    }

    let searchElement: HTMLDivElement;
    let inputElem: HTMLInputElement;
    let resultsElem: HTMLDivElement;

    function onKeydown(event: KeyboardEvent) {
        event.stopPropagation();

        if (event.key === "ArrowDown") {
            event.preventDefault();
            selectedIndex = (selectedIndex + 1) % results.length;
            return;
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            selectedIndex = (selectedIndex - 1 + results.length) % results.length;
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            executeSelected();
            return;
        }
        if (event.key === "Tab") {
            if (!tabs) return;
            event.preventDefault();

            if (event.shiftKey) {
                tabIndex = (tabIndex - 1 + tabs.length) % tabs.length;
            } else {
                tabIndex = (tabIndex + 1) % tabs.length;
            }
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            close();
            return;
        }
    }

    function onScroll(event: Event) {
        event.stopPropagation();
    }

    function onQueryChange(query: string) {
        selectedIndex = 0;
    }

    function selectAndExecute(i: number) {
        selectedIndex = i;
        executeSelected();
    }

    function executeSelected() {
        results[selectedIndex].obj.action();
        close();
    }

    function scrollSelectedIntoView(selectedIndex: number) {
        if (!open) return;
        const target = resultsElem?.childNodes?.[selectedIndex] as HTMLElement;
        target?.scrollIntoView?.({
            block: "nearest"
        });
    }

    function onFocusIn(event: FocusEvent) {
        if (event.target === inputElem) return;
        inputElem.focus();
    }

    function onFocusOut(event: FocusEvent) {
        if (searchElement.contains(event.relatedTarget as Node)) return;
        close();
    }

    function onOpen() {
        setTimeout(() => {
            inputElem?.focus();
            inputElem?.select();
        }, 0);
    }

    function close() {
        open = false;
    }

    let itemTemplate = item ?? defaultItem;
</script>

<!----------------------------------------------------------------------------->

<div
  bind:this={searchElement}
  class="search"
  class:open
  onkeydown={onKeydown}
  onwheel={onScroll}
  onfocusin={onFocusIn}
  onfocusout={onFocusOut}
  tabindex="-1"
  role="menu"
>
  {#if tabs}
    <Tabs bind:index={tabIndex} {tabs} />
  {/if}
  <input bind:this={inputElem} role="searchbox" type="text" bind:value={query} />
  <div bind:this={resultsElem} class="results" style="--items: {maxItems};">
    {#each results as result, i (result.target)}
      {@const selected = i === selectedIndex}
      <button
        class="result"
        class:selected
        onclick={() => selectAndExecute(i)}
        onkeydown={(e) => e.preventDefault()}
        disabled={result.disabled}
      >
        {@render itemTemplate({
          item: result.obj,
          match: result.name,
          group: result.group,
          selected,
          tab: tabIndex
        })}
      </button>
    {/each}
  </div>
</div>

{#snippet defaultItem({ match, group }: ItemTemplateInput)}
  <div>
    {@html group}
    {@html match}
  </div>
{/snippet}

<!----------------------------------------------------------------------------->

<style>
    .search {
        display: none;
        position: absolute;
        width: min(40em, 80vw);
        background: var(--panel-bg);
        border: 1px solid var(--gap-color-hard);
        transform: translate(-50%, -50%);
        left: 50%;
        top: 50%;
    }

    .open {
        display: block;
    }

    .search :global([role="tablist"]) {
        border-bottom: 1px solid var(--gap-color-hard);
    }

    input {
        all: unset;
        width: 100%;
        padding-inline: 0.5em;
        box-sizing: border-box;
        border-bottom: 1px solid var(--gap-color-hard);
        line-height: 2.25em;
    }

    .results {
        --item-height: 1.5em;

        display: flex;
        flex-direction: column;
        height: calc(var(--items) * var(--item-height));

        scrollbar-width: thin;
        scrollbar-gutter: stable;
        overflow-y: scroll;
    }

    .result {
        all: unset;
        height: var(--item-height);
        padding-inline: 0.5em;
        cursor: pointer;
    }

    .result[disabled] {
        color: var(--t-disabled);
    }

    .selected {
        background: var(--button-hover);
    }
</style>
